import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, Star, Zap, Crown, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const packages = [
  {
    name: 'Starter',
    price: '1290',
    description: 'Idéal pour les indépendants et petites structures.',
    icon: Zap,
    features: [
      'Site vitrine jusqu\'à 5 pages',
      'Design responsive',
      'Formulaire de contact',
      'Configuration technique du site',
      'Sécurité standard',
      'Mise en ligne',
    ],
    cta: 'Choisir Starter',
    popular: false,
  },
  {
    name: 'Business',
    price: '2490',
    description: 'Parfait pour les PME et entreprises en croissance.',
    icon: Star,
    features: [
      'Site jusqu\'à 15 pages',
      'Design sur mesure',
      'Animations et interactions',
      'Blog intégré',
      'Configuration technique complète',
      'Sécurité avancée',
      'Mise en ligne',
    ],
    cta: 'Choisir Business',
    popular: true,
  },
  {
    name: 'Premium',
    price: '4490',
    description: 'Pour les entreprises qui veulent un projet plus complet et évolutif.',
    icon: Crown,
    features: [
      'Site avec structure étendue',
      'Design exclusif sur mesure',
      'Fonctionnalités avancées',
      'Espace client / Dashboard',
      'Multilingue',
      'Sécurité professionnelle',
      'Maintenance offerte 3 mois',
    ],
    cta: 'Choisir Premium',
    popular: false,
  },
  {
    name: 'Sur mesure',
    price: 'Devis',
    description: 'Projets complexes et spécifiques.',
    icon: Sparkles,
    features: [
      'Analyse des besoins',
      'Architecture personnalisée',
      'Développement sur mesure',
      'Intégrations API',
      'Applications web ou mobile',
      'Fonctionnalités spécifiques',
      'Évolutions possibles',
    ],
    cta: 'Demander un devis',
    popular: false,
  },
];

const subscriptions = [
  {
    name: 'Standard',
    price: '59,99',
    period: '/mois',
    description: 'Pour les sites vitrines et les petites structures qui veulent un site maintenu et sécurisé.',
    features: [
      'Maintenance technique mensuelle',
      'Sauvegardes mensuelles',
      'Sécurité standard (HTTPS / SSL)',
      'Corrections de bugs mineurs',
      'Jusqu\'à 5 modifications majeurs / mois*',
      'Hébergement inclus',
      'Support email (délai standard)',
    ],
    cta: 'Choisir Standard',
    popular: false,
  },
  {
    name: 'Plus',
    price: '79,99',
    period: '/mois',
    description: 'Une solution équilibrée pour les entreprises qui veulent un site régulièrement mis à jour.',
    features: [
      'Maintenance technique mensuelle',
      'Sauvegardes hebdomadaires',
      'Sécurité avancée',
      'Corrections de bugs',
      'Jusqu\'à 15 modifications majeures / mois*',
      'Interventions majeures prioritaires',
      'Hébergement inclus',
      'Support email (réponse prioritaire)',
    ],
    cta: 'Choisir Plus',
    popular: false,
  },
  {
    name: 'Premium',
    price: '99,99',
    period: '/mois',
    description: 'Pour les entreprises qui veulent un suivi complet, évolutif et prioritaire.',
    features: [
      'Maintenance technique continue',
      'Sauvegardes quotidiennes + backup actif',
      'Sécurité professionnelle renforcée',
      'Monitoring du site (disponibilité et performances)',
      'Corrections de bugs prioritaires',
      'Jusqu\'à 30 modifications majeures / mois*',
      'Modifications majeures possibles sur devis',
      'Hébergement inclus',
      'Support prioritaire',
    ],
    cta: 'Choisir Premium',
    popular: true,
  },
];

const pricingFaqs = [
  {
    q: 'Les tarifs sont-ils définitifs ?',
    a: 'Nos tarifs sont indicatifs. Chaque projet étant unique, nous établissons un devis personnalisé après analyse de vos besoins spécifiques.',
  },
  {
    q: 'Quels sont les délais de réalisation ?',
    a: 'Les délais varient selon la complexité du projet. Un site vitrine prend généralement 2 à 4 semaines, un site e-commerce 4 à 8 semaines.',
  },
  {
    q: 'Proposez-vous des facilités de paiement ?',
    a: 'Oui, nous proposons un paiement en plusieurs fois : 40% à la commande, 30% à la validation des maquettes, 30% à la livraison.',
  },
  {
    q: 'Que comprend la maintenance ?',
    a: 'Notre maintenance inclut les mises à jour techniques, les sauvegardes régulières, le monitoring, l’hébergement, la sécurité et les corrections de bugs selon le niveau d’abonnement choisi.',
  },
  {
    q: 'Que comprennent les modifications incluses ?',
    a: 'Les modifications incluses concernent les ajustements mineurs comme les textes, images, petites sections ou corrections simples. Les nouvelles pages ou fonctionnalités avancées peuvent faire l’objet d’un devis complémentaire.',
  },
];

export const Pricing = () => {
  const pageTitle =
    'Tarifs Weblax | Prix création de site internet, SEO, maintenance et accompagnement digital';
  const pageDescription =
    'Découvrez les tarifs Weblax pour la création de site internet, le référencement SEO, la maintenance, le support, les abonnements et les solutions digitales sur mesure. Devis personnalisé selon votre projet.';
  const pageUrl = 'https://weblax.fr/tarifs';
  const pageImage = 'https://weblax.fr/og-image.jpg';

  const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Tarifs Weblax',
    url: pageUrl,
    description: pageDescription,
  };

  const offerCatalogStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'OfferCatalog',
    name: 'Tarifs et offres Weblax',
    itemListElement: [
      ...packages.map((pkg) => ({
        '@type': 'Offer',
        name: pkg.name,
        description: pkg.description,
        price: pkg.price === 'Devis' ? undefined : pkg.price,
        priceCurrency: pkg.price === 'Devis' ? undefined : 'EUR',
        url: 'https://weblax.fr/devis',
        category: 'Création de site internet',
      })),
      ...subscriptions.map((sub) => ({
        '@type': 'Offer',
        name: sub.name,
        description: sub.description,
        price: sub.price.replace(',', '.'),
        priceCurrency: 'EUR',
        url: 'https://weblax.fr/devis',
        category: 'Abonnement',
      })),
    ],
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: pricingFaqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Weblax',
    url: 'https://weblax.fr',
    logo: 'https://weblax.fr/logo.png',
    email: 'contact@weblax.fr',
    telephone: '+33685802016',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Paris',
      addressCountry: 'FR',
    },
  };

  return (
    <>
      <Helmet>
        <html lang="fr" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="tarifs création site internet, prix site vitrine, prix site e-commerce, tarif SEO, maintenance site web, abonnement site internet, support site web, devis site internet, agence web tarifs, Weblax"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Weblax" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={pageImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        <script type="application/ld+json">
          {JSON.stringify(webPageStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(offerCatalogStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationStructuredData)}
        </script>
      </Helmet>

      <div data-testid="pricing-page">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-background">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.1),transparent_50%)]" />
          <div className="weblax-container relative">
            <div className="py-20 md:py-28 text-center">
              <p className="weblax-caption text-primary mb-4">Tarifs</p>
              <h1 className="weblax-heading-display mb-6 max-w-3xl mx-auto">
                Des offres claires et <span className="weblax-gradient-text">transparentes</span>
              </h1>
              <p className="weblax-body-lg max-w-2xl mx-auto">
                Choisissez l&apos;offre adaptée à vos besoins. Tous nos tarifs sont indicatifs,
                un devis personnalisé vous sera proposé selon votre projet.
              </p>
            </div>
          </div>
        </section>

        {/* Tabs Section */}
        <section className="weblax-section">
          <div className="weblax-container">
            <Tabs defaultValue="packages" className="w-full">
              <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12">
                <TabsTrigger value="packages" data-testid="tab-packages">
                  Offres ponctuelles
                </TabsTrigger>
                <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">
                  Abonnements
                </TabsTrigger>
              </TabsList>

              {/* Packages Tab */}
              <TabsContent value="packages">
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
                  {packages.map((pkg, index) => (
                    <Card
                      key={index}
                      className={`weblax-card relative h-full flex flex-col ${pkg.popular ? 'border-primary ring-2 ring-primary/20 shadow-lg' : ''
                        }`}
                    >
                      {pkg.popular && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                          Populaire
                        </Badge>
                      )}

                      <CardHeader className="text-center pb-4 flex flex-col min-h-[220px]">
                        <div
                          className={`w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center ${pkg.popular ? 'bg-primary' : 'bg-primary/10'
                            }`}
                        >
                          <pkg.icon className={`w-7 h-7 ${pkg.popular ? 'text-white' : 'text-primary'}`} />
                        </div>

                        <h3 className="font-semibold text-xl min-h-[32px] flex items-center justify-center">
                          {pkg.name}
                        </h3>

                        <div className="mt-4 min-h-[72px] flex items-center justify-center">
                          {pkg.price === 'Devis' ? (
                            <span className="text-3xl font-bold">Sur devis</span>
                          ) : (
                            <div className="flex items-end justify-center gap-1">
                              <span className="text-sm text-muted-foreground mb-1">à partir de</span>
                              <span className="text-4xl font-bold leading-none">{pkg.price}</span>
                              <span className="text-lg text-muted-foreground mb-1">€</span>
                            </div>
                          )}
                        </div>

                        <p className="text-sm text-muted-foreground mt-3 min-h-[48px] flex items-start justify-center text-center">
                          {pkg.description}
                        </p>
                      </CardHeader>

                      <CardContent className="flex flex-col flex-1">
                        <ul className="space-y-3 mb-6 flex-1">
                          {pkg.features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm">
                              <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <Link to="/devis" className="block mt-auto">
                          <Button
                            className={`w-full ${pkg.popular ? 'weblax-btn-primary' : 'weblax-btn-secondary'
                              }`}
                            data-testid={`pricing-cta-${pkg.name.toLowerCase().replace(/\s/g, '-')}`}
                          >
                            {pkg.cta}
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Informations complémentaires offres ponctuelles */}
                <div className="mt-8 space-y-4 max-w-4xl mx-auto">
                  <Card className="weblax-card border-dashed">
                    <CardContent className="p-6 text-center">
                      <p className="text-sm text-muted-foreground">
                        Les offres de création de site n’incluent pas l’hébergement ni la maintenance continue.
                        Un abonnement Weblax est requis pour l’hébergement, la maintenance, les sauvegardes,
                        le support technique et le suivi du site.
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="weblax-card border-primary/20 bg-primary/5">
                    <CardContent className="p-6 text-center">
                      <p className="text-sm md:text-base text-foreground">
                        Pour vous offrir une solution de paiement plus souple, les offres ponctuelles peuvent être
                        réglées en <span className="font-semibold text-primary">jusqu&apos;à 3 fois sans frais ni intérêts</span>,
                        selon le montant du projet et après validation du devis.
                      </p>
                    </CardContent>
                  </Card>

                  <div className="px-2">
                    <p className="text-xs text-muted-foreground text-center leading-relaxed">
                      * Modalités de paiement : sauf mention contraire sur le devis, le règlement des offres ponctuelles
                      s&apos;effectue selon l&apos;échéancier convenu lors de la commande. Le client s&apos;engage à respecter
                      les dates de versement prévues. En cas de retard ou de défaut de paiement, Weblax se réserve le droit
                      de suspendre temporairement les travaux, la mise en ligne, la livraison du projet ou les prestations
                      associées jusqu&apos;à régularisation complète de la situation.
                    </p>
                  </div>
                </div>
              </TabsContent>

              {/* Subscriptions Tab */}
              <TabsContent value="subscriptions">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
                  {subscriptions.map((sub, index) => (
                    <Card
                      key={index}
                      className={`weblax-card relative h-full flex flex-col ${sub.popular ? 'border-primary ring-2 ring-primary/20 shadow-lg' : ''
                        }`}
                    >
                      {sub.popular && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                          Recommandé
                        </Badge>
                      )}

                      <CardHeader className="text-center pb-4 flex flex-col min-h-[220px]">
                        <h3 className="font-semibold text-xl min-h-[32px] flex items-center justify-center">
                          {sub.name}
                        </h3>

                        <div className="mt-4 min-h-[72px] flex items-center justify-center">
                          <div className="flex items-end justify-center gap-1">
                            <span className="text-4xl font-bold leading-none">{sub.price}</span>
                            <span className="text-lg text-muted-foreground mb-1">€{sub.period}</span>
                          </div>
                        </div>

                        <p className="text-sm text-muted-foreground mt-3 min-h-[48px] flex items-start justify-center text-center">
                          {sub.description}
                        </p>
                      </CardHeader>

                      <CardContent className="flex flex-col flex-1">
                        <ul className="space-y-3 mb-6 flex-1">
                          {sub.features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm">
                              <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <Link to="/devis" className="block mt-auto">
                          <Button
                            className={`w-full ${sub.popular ? 'weblax-btn-primary' : 'weblax-btn-secondary'
                              }`}
                          >
                            {sub.cta}
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="mt-8 max-w-4xl mx-auto">
                  <Card className="weblax-card border-dashed">
                    <CardContent className="p-6 text-center">
                      <p className="text-sm text-muted-foreground">
                        * Les modifications incluses concernent les ajustements mineurs
                        (textes, images, petites sections ou corrections simples).
                        Les nouvelles pages, refontes importantes ou fonctionnalités avancées
                        peuvent faire l&apos;objet d&apos;un devis complémentaire.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="weblax-section bg-card">
          <div className="weblax-container">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="weblax-heading-1 mb-4">Questions fréquentes</h2>
              <p className="weblax-body-lg">
                Retrouvez les réponses aux questions les plus courantes sur nos tarifs.
              </p>
            </div>
            <div className="max-w-3xl mx-auto grid gap-6">
              {pricingFaqs.map((faq, index) => (
                <Card key={index} className="weblax-card">
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-2">{faq.q}</h4>
                    <p className="text-muted-foreground text-sm">{faq.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/faq">
                <Button variant="outline" className="weblax-btn-secondary">
                  Voir toutes les FAQ
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="weblax-section">
          <div className="weblax-container">
            <div className="bg-primary rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.1),transparent_50%)]" />
              <div className="relative">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Besoin d&apos;un devis personnalisé ?
                </h2>
                <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
                  Décrivez-nous votre projet et recevez une proposition adaptée à vos besoins et votre budget.
                </p>
                <Link to="/devis">
                  <Button className="bg-white text-primary hover:bg-white/90 px-8 py-3 rounded-full font-medium">
                    Demander un devis gratuit
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Pricing;